import React, { useRef, useEffect } from 'react';
import * as THREE from 'three';
import useIsMobile from '../../hooks/useIsMobile';

/** About page — neural-network / constellation effect.
 *  3D web of nodes connected by thin lines; nodes near cursor light up.
 */
const NODE_COUNT = 70;
const LINK_DISTANCE = 3.4;

const NeuralNetworkBg = () => {
    const mountRef = useRef(null);
    const isMobile = useIsMobile();

    useEffect(() => {
        if (isMobile) return;
        const mount = mountRef.current;
        if (!mount) return;
        const w = mount.clientWidth, h = mount.clientHeight;

        const scene = new THREE.Scene();
        const camera = new THREE.PerspectiveCamera(60, w / h, 0.1, 100);
        camera.position.z = 14;

        const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        renderer.setSize(w, h);
        renderer.setClearColor(0x000000, 0);
        mount.appendChild(renderer.domElement);

        const colours = [0x7B61FF, 0x00E5CC, 0xFFFFFF, 0xFFB3D9];

        // Nodes
        const nodeGroup = new THREE.Group();
        const nodes = [];
        for (let i = 0; i < NODE_COUNT; i++) {
            const colour = colours[Math.floor(Math.random() * colours.length)];
            const geom = new THREE.SphereGeometry(0.08, 12, 10);
            const mat = new THREE.MeshBasicMaterial({
                color: colour,
                transparent: true,
                opacity: 0.85,
            });
            const m = new THREE.Mesh(geom, mat);
            m.position.set(
                (Math.random() - 0.5) * 14,
                (Math.random() - 0.5) * 9,
                (Math.random() - 0.5) * 7
            );
            m.userData = {
                base: m.position.clone(),
                drift: new THREE.Vector3(
                    (Math.random() - 0.5) * 0.5,
                    (Math.random() - 0.5) * 0.5,
                    (Math.random() - 0.5) * 0.5
                ),
                phase: Math.random() * Math.PI * 2,
                baseScale: 1,
            };
            nodes.push(m);
            nodeGroup.add(m);
        }
        scene.add(nodeGroup);

        // Link buffer
        const linkGeo = new THREE.BufferGeometry();
        const maxLinks = 600;
        const linkPositions = new Float32Array(maxLinks * 6);
        const linkColors = new Float32Array(maxLinks * 6);
        linkGeo.setAttribute('position', new THREE.BufferAttribute(linkPositions, 3));
        linkGeo.setAttribute('color', new THREE.BufferAttribute(linkColors, 3));
        const linkMat = new THREE.LineBasicMaterial({
            vertexColors: true,
            transparent: true,
            opacity: 0.55,
        });
        const linkLines = new THREE.LineSegments(linkGeo, linkMat);
        scene.add(linkLines);

        // Mouse
        const mouse3 = new THREE.Vector3(0, 0, 0);
        const mouseScreen = { x: 0, y: 0 };
        const onMouseMove = (e) => {
            const rect = mount.getBoundingClientRect();
            mouseScreen.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
            mouseScreen.y = -(((e.clientY - rect.top) / rect.height) * 2 - 1);
        };
        window.addEventListener('mousemove', onMouseMove);

        const onResize = () => {
            const W = mount.clientWidth, H = mount.clientHeight;
            renderer.setSize(W, H);
            camera.aspect = W / H;
            camera.updateProjectionMatrix();
        };
        window.addEventListener('resize', onResize);

        const tmpV = new THREE.Vector3();
        const clock = new THREE.Clock();
        let raf = 0;
        const tick = () => {
            raf = requestAnimationFrame(tick);
            const t = clock.getElapsedTime();

            // Project mouse to world at z=0
            mouse3.set(mouseScreen.x, mouseScreen.y, 0.5).unproject(camera);
            const dir = mouse3.clone().sub(camera.position).normalize();
            const dist = -camera.position.z / dir.z;
            const hit = camera.position.clone().add(dir.multiplyScalar(dist));

            // Drift + cursor lift on nodes
            nodes.forEach((n) => {
                const d = n.userData;
                n.position.x = d.base.x + Math.sin(t * 0.3 + d.phase) * d.drift.x;
                n.position.y = d.base.y + Math.cos(t * 0.3 + d.phase) * d.drift.y;
                n.position.z = d.base.z + Math.sin(t * 0.25 + d.phase) * d.drift.z;
                const distToMouse = n.position.distanceTo(hit);
                const lit = Math.max(0, 1 - distToMouse / 2.2);
                n.material.opacity = 0.55 + lit * 0.45;
                const s = 1 + lit * 1.5;
                n.scale.setScalar(s);
            });

            // Build links (only near pairs)
            let li = 0;
            const linkArr = linkGeo.attributes.position.array;
            const colArr = linkGeo.attributes.color.array;
            for (let i = 0; i < nodes.length && li < maxLinks; i++) {
                for (let j = i + 1; j < nodes.length && li < maxLinks; j++) {
                    tmpV.subVectors(nodes[i].position, nodes[j].position);
                    const d = tmpV.length();
                    if (d < LINK_DISTANCE) {
                        const fade = 1 - d / LINK_DISTANCE;
                        const midDist = Math.min(
                            nodes[i].position.distanceTo(hit),
                            nodes[j].position.distanceTo(hit)
                        );
                        const litFactor = Math.max(0, 1 - midDist / 2.5);
                        linkArr[li * 6] = nodes[i].position.x;
                        linkArr[li * 6 + 1] = nodes[i].position.y;
                        linkArr[li * 6 + 2] = nodes[i].position.z;
                        linkArr[li * 6 + 3] = nodes[j].position.x;
                        linkArr[li * 6 + 4] = nodes[j].position.y;
                        linkArr[li * 6 + 5] = nodes[j].position.z;
                        const c = 0.5 + litFactor * 0.5;
                        // Violet-ish base, lighter when near cursor
                        const r = 0.48 + 0.4 * litFactor;
                        const g = 0.38 + 0.55 * litFactor;
                        const b = 1.0;
                        for (let k = 0; k < 2; k++) {
                            colArr[li * 6 + k * 3] = r * fade * c;
                            colArr[li * 6 + k * 3 + 1] = g * fade * c;
                            colArr[li * 6 + k * 3 + 2] = b * fade * c;
                        }
                        li++;
                    }
                }
            }
            // Zero-out unused
            for (let i = li; i < maxLinks; i++) {
                for (let k = 0; k < 6; k++) linkArr[i * 6 + k] = 0;
            }
            linkGeo.attributes.position.needsUpdate = true;
            linkGeo.attributes.color.needsUpdate = true;
            linkGeo.setDrawRange(0, li * 2);

            nodeGroup.rotation.y = t * 0.06;
            nodeGroup.rotation.x = Math.sin(t * 0.1) * 0.2;
            linkLines.rotation.copy(nodeGroup.rotation);

            renderer.render(scene, camera);
        };
        tick();

        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('resize', onResize);
            if (mount && renderer.domElement.parentNode === mount) {
                mount.removeChild(renderer.domElement);
            }
            nodes.forEach((n) => {
                n.geometry.dispose();
                n.material.dispose();
            });
            linkGeo.dispose();
            linkMat.dispose();
            renderer.dispose();
        };
    }, [isMobile]);

    if (isMobile) {
        return (
            <div
                className="absolute inset-0 pointer-events-none"
                style={{
                    background:
                        'radial-gradient(circle at 30% 40%, rgba(37,99,235,0.12), transparent 55%), radial-gradient(circle at 70% 60%, rgba(56,189,248,0.12), transparent 55%), radial-gradient(circle at 50% 50%, rgba(96,165,250,0.08), transparent 60%)',
                }}
                aria-hidden="true"
            />
        );
    }

    return (
        <div
            ref={mountRef}
            className="absolute inset-0 w-full h-full pointer-events-none"
            aria-hidden="true"
            data-testid="bg-neural-network"
        />
    );
};

export default NeuralNetworkBg;
